import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    Tooltip,
    Paper,
    IconButton,
    TextField,
    Autocomplete,
    Box
} from '@mui/material';
import { HelpOutline as HelpIcon, CheckCircle as CheckCircleIcon, Edit as EditIcon, Check as ConfirmIcon, Close as CancelIcon } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';

// Reuse normalization logic from BulkImageUpload
function normalize(str: string): string {
    return str
        .toLowerCase()
        .replace(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i, '')
        .replace(/[-_]/g, ' ')
        .trim();
}

interface ProductName { productId: number; name: string }

interface Props {
    open: boolean;
    files: File[]; // Updated to accept multiple files
    currentProductName: string;
    allProducts?: ProductName[]; 
    onClose: () => void;
    onConfirm: (files: File[], newNames: string[]) => void; // Updated to return arrays
    uploading: boolean;
}

export const ImageUploadValidationDialog: React.FC<Props> = ({
    open,
    files,
    currentProductName,
    allProducts = [],
    onClose,
    onConfirm,
    uploading
}) => {
    const [fileData, setFileData] = useState<Array<{ 
        file: File, 
        displayName: string, 
        previewUrl: string,
        editValue: string,
        isEditing: boolean
    }>>([]);

    useEffect(() => {
        if (files && files.length > 0) {
            const newData = files.map(file => ({
                file,
                displayName: file.name,
                previewUrl: URL.createObjectURL(file),
                editValue: file.name,
                isEditing: false
            }));
            setFileData(newData);
            return () => {
                newData.forEach(d => URL.revokeObjectURL(d.previewUrl));
            };
        } else {
            setFileData([]);
        }
    }, [files, open]);

    const getMatch = (displayName: string) => {
        const fileKey = normalize(displayName);
        if (!fileKey) return null;

        if (normalize(currentProductName) === fileKey) {
            return { name: currentProductName, isCurrent: true };
        }

        const match = allProducts.find(p => normalize(p.name) === fileKey);
        if (match) {
            return { name: match.name, isCurrent: false };
        }

        return null;
    };

    const handleRename = (index: number, newName: string) => {
        setFileData(prev => prev.map((d, i) => i === index ? { ...d, displayName: newName, isEditing: false } : d));
    };

    const toggleEditing = (index: number, editing: boolean) => {
        setFileData(prev => prev.map((d, i) => i === index ? { ...d, isEditing: editing, editValue: d.displayName } : d));
    };

    const handleEditValueChange = (index: number, value: string) => {
        setFileData(prev => prev.map((d, i) => i === index ? { ...d, editValue: value } : d));
    };

    if (!files || files.length === 0) return null;

    return (
        <Dialog open={open} onClose={uploading ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle className="!font-bold !pb-2">
                Confirm {files.length > 1 ? 'Images' : 'Image'} Upload
                <div className="text-sm font-normal text-gray-500 mt-1">
                    Target Product: <span className="font-semibold text-gray-800">{currentProductName}</span>
                </div>
            </DialogTitle>
            <DialogContent>
                <p className="text-sm text-gray-600 mb-4">
                    Please review the {files.length > 1 ? 'image matches' : 'image match'} before uploading.
                </p>
                <Paper className="!shadow-sm !border !border-gray-200 overflow-hidden">
                    <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow className="bg-gray-100">
                                    <TableCell className="!font-semibold bg-gray-100">Preview</TableCell>
                                    <TableCell className="!font-semibold bg-gray-100">File Name</TableCell>
                                    <TableCell className="!font-semibold bg-gray-100">Matched Product</TableCell>
                                    <TableCell className="!font-semibold bg-gray-100">Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {fileData.map((data, index) => {
                                    const matchResult = getMatch(data.displayName);
                                    const isMatched = !!matchResult;
                                    const isCurrentMatch = matchResult?.isCurrent || false;
                                    const matchedName = matchResult?.name || '';

                                    return (
                                        <TableRow key={index} hover>
                                            <TableCell>
                                                <div className="relative group p-1">
                                                    <img
                                                        src={data.previewUrl}
                                                        alt={data.file.name}
                                                        className="w-16 h-16 object-contain rounded border border-gray-200 bg-white"
                                                    />
                                                    <span className="absolute -top-1 -left-1 bg-blue-500 text-white text-[9px] font-bold px-1 rounded shadow-sm">NEW</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {data.isEditing ? (
                                                    <div className="flex items-center gap-1">
                                                        <Autocomplete
                                                            freeSolo
                                                            options={Array.from(new Set(allProducts.map((p) => p.name)))}
                                                            inputValue={data.editValue}
                                                            onInputChange={(_, val) => handleEditValueChange(index, val)}
                                                            onChange={(_, val) => { if (val) handleEditValueChange(index, val); }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') handleRename(index, data.editValue);
                                                                if (e.key === 'Escape') toggleEditing(index, false);
                                                            }}
                                                            sx={{ minWidth: 200 }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    size="small"
                                                                    placeholder="Type or search product..."
                                                                    autoFocus
                                                                />
                                                            )}
                                                        />
                                                        <Tooltip title="Confirm">
                                                            <IconButton size="small" color="success" onClick={() => handleRename(index, data.editValue)}>
                                                                <ConfirmIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Cancel">
                                                            <IconButton size="small" onClick={() => toggleEditing(index, false)}>
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex flex-col">
                                                            <span className="text-sm font-medium text-gray-700">{data.displayName}</span>
                                                            <span className="text-xs text-gray-400">
                                                                {(data.file.size / 1024).toFixed(1)} KB
                                                            </span>
                                                        </div>
                                                        <Tooltip title="Rename file to fix match">
                                                            <span>
                                                                <AppButton
                                                                    variant="secondary"
                                                                    size="small"
                                                                    startIcon={<EditIcon />}
                                                                    onClick={() => toggleEditing(index, true)}
                                                                    disabled={uploading}
                                                                    className="!py-0.5 !px-2 !text-[11px] !h-7"
                                                                >
                                                                    Rename
                                                                </AppButton>
                                                            </span>
                                                        </Tooltip>
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {isMatched ? (
                                                    <span className="text-sm font-medium text-gray-800">
                                                        {matchedName}
                                                    </span>
                                                ) : (
                                                    <span className="text-sm text-orange-500 italic">No matching product found</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {isMatched && isCurrentMatch ? (
                                                    <Chip label="Match" size="small" color="success" icon={<CheckCircleIcon />} variant="filled" />
                                                ) : isMatched ? (
                                                    <Tooltip title={`Matches another product: ${matchedName}`}>
                                                        <Chip label="Partial match" size="small" color="info" icon={<HelpIcon />} variant="filled" />
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="Filename does not match this product name exactly">
                                                        <Chip label="No Match" size="small" color="warning" icon={<HelpIcon />} variant="filled" />
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                </Paper>
            </DialogContent>
            <DialogActions className="p-4 bg-gray-50 border-t">
                <AppButton variant="secondary" onClick={onClose} disabled={uploading}>
                    Cancel
                </AppButton>
                <AppButton
                    variant="primary"
                    onClick={() => {
                        const newNames = fileData.map(d => {
                            let finalName = d.displayName;
                            const extMatch = d.file.name.match(/\.[^.]+$/);
                            const ext = extMatch ? extMatch[0] : '';
                            if (ext && !finalName.endsWith(ext)) {
                                finalName += ext;
                            }
                            return finalName;
                        });
                        onConfirm(files, newNames);
                    }}
                    disabled={uploading}
                    loading={uploading}
                >
                    {uploading ? 'Uploading...' : `Confirm & Upload ${files.length > 1 ? files.length + ' Images' : 'Image'}`}
                </AppButton>
            </DialogActions>
        </Dialog>
    );
};
